<template>
  <div class="draft-menu">
    <!-- Main draft selector bar -->
    <div class="draft-menu-bar">
      <!-- Draft status indicator -->
      <div class="draft-status">
        <span v-if="isAutoSaving" class="status saving">
          <i class="fa fa-circle-notch fa-spin"></i> {{ $t('Saving...') }}
        </span>
        <span v-else-if="isSaved" class="status saved">
          <i class="fa fa-check-circle"></i> 
        </span>
        <span v-else class="status unsaved">
          <i class="fa fa-exclamation-circle"></i> {{ $t('Unsaved') }}
        </span>
      </div>
      
      <!-- Version menu dropdown -->
      <div class="draft-dropdown">
        <button class="draft-dropdown-btn" @click="toggleVersionsMenu">
          <span class="version-label">{{ getCurrentVersionLabel }}</span>
          <i class="fa fa-caret-down"></i>
        </button>
        
        <div class="draft-dropdown-menu" v-if="showVersionsMenu">
              
          <div class="draft-menu-item" 
               :class="{ active: currentDraftId === 'original' }"
               @click="selectDraft('original')">
            <i class="fa fa-file menu-icon"></i>
            <span>{{ $t('Original Version') }}</span>
          </div>
          
          <div v-if="availableDrafts.length > 0" class="draft-menu-section">
            <span>{{ $t('Saved Drafts') }}</span>
          </div>
          
          <div v-for="draft in availableDrafts" 
               :key="draft.versionId"
               class="draft-menu-item"
               :class="{ active: currentDraftId === draft.versionId }">
            <div class="draft-item-content" @click="selectDraft(draft.versionId)">
              <i class="fa fa-flag menu-icon"></i>
              <span>{{ draft.name || formatDate(draft.createdAt) }}</span>
            </div>
            <button 
              v-if="currentDraftId !== draft.versionId && draftToDelete !== draft.versionId" 
              class="delete-draft-btn" 
              @click.stop="confirmDeleteDraft(draft.versionId)"
              title="Delete Draft">
              <i class="fa fa-trash"></i>
            </button>
            <!-- Delete confirmation directly in the menu -->
            <div v-if="draftToDelete === draft.versionId" class="delete-confirm-menu">
              <span>{{ $t('Delete?') }}</span>
              <button 
                class="confirm-yes-btn" 
                @click.stop="deleteDraft()"
                :disabled="isDeleting">
                <i v-if="isDeleting" class="fa fa-circle-notch fa-spin"></i>
                <i v-else class="fa fa-check"></i>
              </button>
              <button 
                class="confirm-no-btn" 
                @click.stop="cancelDeleteDraft()">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="draft-actions">
        <button @click="showCheckpointDialog = true" class="menu-button save-btn" title="New Draft">
          <i class="fa fa-star"></i> {{ $t('New') }}
        </button>
      </div>
    </div>
  
  </div>
  
  <!-- Checkpoint creation dialog -->
  <div v-if="showCheckpointDialog" class="checkpoint-dialog-overlay">
    <div class="checkpoint-dialog">
      <div class="checkpoint-dialog-header">
        <h4>{{ $t('New Version') }}</h4>
        <button @click="showCheckpointDialog = false" class="close-btn">
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div class="checkpoint-dialog-body">
        <div class="form-group">
          <label for="checkpoint-name">{{ $t('Version Name') }}</label>
          <input 
            id="checkpoint-name"
            v-model="checkpointName" 
            :placeholder="$t('E.g., After timing adjustments')" 
            class="form-control"
            @keyup.enter="saveCheckpoint"
          />
        </div>
        
        <div class="form-group">
          <label for="checkpoint-description">{{ $t('Description (Optional)') }}</label>
          <textarea 
            id="checkpoint-description"
            v-model="checkpointDescription" 
            :placeholder="$t('Notes about this version')" 
            class="form-control"
            rows="2"
          ></textarea>
        </div>
      </div>
      
      <div class="checkpoint-dialog-footer">
        <button @click="showCheckpointDialog = false" class="btn btn-secondary btn-fill">
          {{ $t('Cancel') }}
        </button>
        <button @click="saveCheckpoint" class="btn btn-success btn-fill" :disabled="isAutoSaving">
          <i v-if="isAutoSaving" class="fa fa-circle-notch fa-spin"></i>
          {{ $t('Save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'DraftSelector',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    editor: {
      type: Object,
      required: true
    }
  },
  emits: ['draft-selected', 'draft-created', 'draft-deleted'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const currentDraftId = ref(props.editor.currentDraftId);
   
    const availableDrafts = ref(props.editor.availableDrafts);
    const isAutoSaving = ref(false);
    const isSaved = ref(true);
    const showCheckpointDialog = ref(false);
    const checkpointName = ref('');
    const checkpointDescription = ref('');
    
    // For dropdown menu
    const showVersionsMenu = ref(false);
    
    // For delete confirmation - now managed inline
    const draftToDelete = ref(null);
    const isDeleting = ref(false);
    
    // Get the current version label for display
    const getCurrentVersionLabel = computed(() => {
      if (currentDraftId.value === 'original') return t('Original Version');
      const draft = availableDrafts.value.find(d => d.versionId === currentDraftId.value);
      return draft ? (draft.name || formatDate(draft.createdAt)) : t('Select Version');
    });
    
    // Toggle the versions dropdown menu
    const toggleVersionsMenu = () => {
      showVersionsMenu.value = !showVersionsMenu.value;
      // Reset delete confirmation when opening/closing menu
      if (!showVersionsMenu.value) {
        draftToDelete.value = null;
      }
    };
    
    // Close menu when clicking outside
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.draft-dropdown');
      if (dropdown && !dropdown.contains(event.target) && showVersionsMenu.value) {
        showVersionsMenu.value = false;
        draftToDelete.value = null; // Reset delete confirmation when closing menu
      }
    };
    
    // Add event listener for clicks outside the menu
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });
    
    // Remove event listener on component destruction
    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });
    
    // Format date helper function
    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(Number(timestamp));
      return date.toLocaleString();
    };
    
    // Load available drafts
    const loadDrafts = async () => {
      if (props.sourceId) {
        try {
          const drafts = await props.editor.fetchDrafts(props.sourceId);
          // Sort drafts by creation time (newest first)
         
        } catch (error) {
          console.error('Error loading drafts:', error);
        }
      }
    };
    
    // Select a draft and emit event
    const selectDraft = async (draftId) => {
      // first we save the current draft
      if (!await props.editor.saveCurrentChanges()) return;
      showVersionsMenu.value = false;
      draftToDelete.value = null;
      emit('draft-selected', draftId);
    };
    
    // Open delete confirmation inline
    const confirmDeleteDraft = (versionId) => {
      draftToDelete.value = versionId;
    };
    
    // Cancel delete operation
    const cancelDeleteDraft = () => {
      draftToDelete.value = null;
    };
    
    // Delete the selected draft
    const deleteDraft = async () => {
      if (!draftToDelete.value || !props.sourceId) return;
      
      isDeleting.value = true;
      
      try {
        if (!await props.editor.deleteDraft(props.sourceId, draftToDelete.value)) return;
        
        // Remove the deleted draft from the local availableDrafts array
        availableDrafts.value = availableDrafts.value.filter(
          draft => draft.versionId !== draftToDelete.value
        );
        
        // If we deleted the currently selected draft, switch to original
        if (currentDraftId.value === draftToDelete.value) {
          currentDraftId.value = 'original';
          emit('draft-selected', 'original');
        }
        
        // Emit event
        emit('draft-deleted', draftToDelete.value);
        
        // Reset delete confirmation state
        draftToDelete.value = null;
      } catch (error) {
        console.error('Error deleting draft:', error);
      } finally {
        isDeleting.value = false;
      }
    };
    
    // Save a new checkpoint
    const saveCheckpoint = async () => {
      if (!checkpointName.value.trim()) {
        // Use default name if none provided
        checkpointName.value = `Checkpoint ${new Date().toLocaleString()}`;
      }
      
      isAutoSaving.value = true;
      
      try {
        // Create a new draft version with the given name and description
        const result = await props.editor.createDraftVersion(
          props.sourceId,
          {
            name: checkpointName.value,
            description: checkpointDescription.value || `Checkpoint created on ${new Date().toLocaleString()}`
          }
        );
        
        // Refresh the drafts list
        await loadDrafts();
        
        // Emit event to notify parent
        if (result && result.success) {
          emit('draft-created', result.versionId);
          
          // Reset dialog
          showCheckpointDialog.value = false;
          checkpointName.value = '';
          checkpointDescription.value = '';
        }
      } catch (error) {
        console.error('Failed to create checkpoint:', error);
      } finally {
        isAutoSaving.value = false;
      }
    };
    
    // Watch for transcript ID changes to reload drafts
    watch(() => props.sourceId, (newId) => {
      if (newId) {
        loadDrafts();
      }
    });
    
    // Watch for auto-save status from editor
    watch(() => props.editor.isAutoSaving, (newValue) => {
      isAutoSaving.value = newValue;
    });
    
    watch(() => props.editor.isSaved, (newValue) => {
      isSaved.value = newValue;
    });
    
    
    // Initialize
    onMounted(() => {
      loadDrafts();
    });
    
    // Expose fetchDrafts method for external use
    const fetchDrafts = async (sourceId) => {
      if (!sourceId) return [];
      
      try {
        const drafts = await props.editor.fetchDrafts(sourceId);
        return drafts || [];
      } catch (error) {
        console.error('Error fetching drafts:', error);
        return [];
      }
    };
    
    return {
      currentDraftId,
      availableDrafts,
      isAutoSaving,
      isSaved,
      isDeleting,
      showCheckpointDialog,
      checkpointName,
      checkpointDescription,
      showVersionsMenu,
      draftToDelete,
      getCurrentVersionLabel,
      toggleVersionsMenu,
      formatDate,
      selectDraft,
      saveCheckpoint,
      confirmDeleteDraft,
      cancelDeleteDraft,
      deleteDraft,
      fetchDrafts // Expose for external use
    };
  }
});
</script>

<style scoped>
/* Main draft menu container */
.draft-menu {
  width: 100%;
  color: var(--color-primary);
  font-size: 0.9rem;
}

/* Menu bar */
.draft-menu-bar {
  display: flex;
  align-items: center;
  background-color: var(--bg-secondary);
  height: 40px;
  border-radius: 6px;
  box-shadow: 0 1px 3px var(--shadow-color);
  padding: 0 10px;
}

/* Status indicator */
.draft-status {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-right: 1px solid var(--border-color);
  height: 100%;
}

.status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  white-space: nowrap;
}

.saving { color: var(--warning-color); }
.saved { color: var(--success-color); }
.unsaved { color: var(--danger-color); }

/* Dropdown menu */
.draft-dropdown {
  position: relative;
  flex: 1;
  height: 100%;
}

.draft-dropdown-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  background: none;
  border: none;
  padding: 0 10px;
  text-align: left;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--color-primary);
}

.version-label {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draft-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  width: 280px;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  box-shadow: 0 4px 10px var(--shadow-color);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
}

.draft-dropdown-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
}

.close-menu-btn {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: var(--color-muted);
}

.draft-menu-section {
  padding: 8px 15px;
  font-size: 0.8rem;
  color: var(--color-muted);
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.draft-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 15px;
  cursor: pointer;
  transition: background-color 0.15s;
  color: var(--color-primary);
}

.draft-item-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.draft-menu-item:hover {
  background-color: var(--bg-secondary);
}

.draft-menu-item.active {
  background-color: var(--highlight-bg);
  font-weight: 500;
}

.menu-icon {
  width: 20px;
  margin-right: 10px;
  color: var(--color-muted);
}

.delete-draft-btn {
  background: none;
  border: none;
  color: var(--color-danger);
  font-size: 0.9rem;
  cursor: pointer;
  opacity: 0.6;
  padding: 2px 5px;
  border-radius: 3px;
  transition: all 0.2s;
}

.delete-draft-btn:hover {
  opacity: 1;
  background-color: rgba(220, 53, 69, 0.1);
}

/* Delete confirmation inline in menu */
.delete-confirm-menu {
  display: flex;
  align-items: center;
  gap: 5px;
}

.delete-confirm-menu span {
  font-size: 0.8rem;
  color: var(--color-danger);
}

.confirm-yes-btn, .confirm-no-btn {
  background: none;
  border: none;
  padding: 2px 5px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.85rem;
}

.confirm-yes-btn {
  color: var(--color-success);
}

.confirm-yes-btn:hover {
  background-color: rgba(40, 167, 69, 0.1);
}

.confirm-no-btn {
  color: var(--color-danger);
}

.confirm-no-btn:hover {
  background-color: rgba(220, 53, 69, 0.1);
}

/* Quick actions */
.draft-actions {
  display: flex;
  align-items: center;
  height: 100%;
  border-left: 1px solid var(--border-color);
  padding-left: 10px;
}

.menu-button {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0 10px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 0.85rem;
  white-space: nowrap;
  transition: background-color 0.2s;
}

.menu-button i {
  margin-right: 5px;
}

.menu-button:hover {
  background-color: var(--bg-hover);
}

.save-btn {
  color: var(--color-success);
}

.save-btn:hover {
  background-color: rgba(40, 167, 69, 0.1);
}

/* Checkpoint dialog overlay */
.checkpoint-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.checkpoint-dialog {
  background-color: var(--bg-primary);
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px var(--shadow-color);
  overflow: hidden;
  color: var(--color-primary);
}

.checkpoint-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
}

.checkpoint-dialog-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--color-primary);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--color-muted);
}

.checkpoint-dialog-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  font-size: 0.9rem;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: var(--bg-primary);
  color: var(--color-primary);
}

.checkpoint-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .draft-menu-bar {
    padding: 0 5px;
  }
  
  .draft-status {
    padding: 0 5px;
  }
  
  .version-label {
    max-width: 150px;
  }
  
  .menu-button {
    padding: 0 5px;
  }
  
  .menu-button span {
    display: none;
  }
}
</style>
